import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  communityHealthResponseSchema,
  type CommunityHealthResponse,
} from "@community-analytics/shared";

import { apiFetch } from "@shared/api";

import { communityQueryKeys } from "./keys";

/**
 * GET /api/communities/:id/health — drives the Overview hero gauge.
 *
 * The response is a discriminated union over `upgradeRequired` (FREE-tier
 * truncation omits breakdown + trend; the gauge is still rendered as a tease
 * with an Upgrade CTA elsewhere). Both branches expose `healthScore`,
 * `label`, `color` so the gauge component itself doesn't have to branch.
 */
export function useHealthQuery(
  communityId: string | null,
): UseQueryResult<CommunityHealthResponse, Error> {
  return useQuery<CommunityHealthResponse, Error>({
    queryKey: communityQueryKeys.health(communityId ?? "__noop__"),
    enabled: communityId !== null,
    queryFn: async () => {
      if (communityId === null) throw new Error("communityId is required");
      const raw = await apiFetch<unknown>(
        `/api/communities/${encodeURIComponent(communityId)}/health`,
      );
      return communityHealthResponseSchema.parse(raw);
    },
  });
}
